import React, { useState } from 'react';
import {
  Building2,
  ArrowRight,
  Sun,
  Zap,
  TrendingDown,
  Users,
  Leaf,
  CheckCircle,
  HeartHandshake,
  Settings,
  FileText,
  Shield,
  BarChart3,
  Target,
  Factory,
  Euro,
  MapPin,
  Calendar,
  ClipboardList,
  Activity,
  PhoneCall,
  Clock,
  Calculator,
  UserPlus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SimulationModal } from '../components/SimulationModal';
import { SEOHead } from '../components/SEOHead';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { EnergySharingDiagram } from '../components/EnergySharingDiagram';
import { NetworkSphere } from '../components/NetworkSphere';

export default function EntreprisePage() {
  const [userRole, setUserRole] = useState<'consumer' | 'producer'>('consumer');
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [simulationInitialRole, setSimulationInitialRole] = useState<'consumer' | 'producer'>('consumer');
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/contact');
  };

  const consumerBenefits = [
    {
      icon: TrendingDown,
      title: t('enterprise.consumer.benefit1Title'),
      description: t('enterprise.consumer.benefit1Desc'),
    },
    {
      icon: Leaf,
      title: t('enterprise.consumer.benefit2Title'),
      description: t('enterprise.consumer.benefit2Desc'),
    },
    {
      icon: Shield,
      title: t('enterprise.consumer.benefit3Title'),
      description: t('enterprise.consumer.benefit3Desc'),
    },
    {
      icon: FileText,
      title: t('enterprise.consumer.benefit4Title'),
      description: t('enterprise.consumer.benefit4Desc'),
    }
  ];

  const producerBenefits = [
    {
      icon: Euro,
      title: t('enterprise.producer.benefit1Title'),
      description: t('enterprise.producer.benefit1Desc'),
    },
    {
      icon: Users,
      title: t('enterprise.producer.benefit2Title'),
      description: t('enterprise.producer.benefit2Desc'),
    },
    {
      icon: Shield,
      title: t('enterprise.producer.benefit3Title'),
      description: t('enterprise.producer.benefit3Desc'),
    },
    {
      icon: Settings,
      title: t('enterprise.producer.benefit4Title'),
      description: t('enterprise.producer.benefit4Desc'),
    }
  ];

  const parcoursSteps = [
    { icon: Calendar, num: 1, title: t('enterprisePage.parcours.step1Title'), desc: t('enterprisePage.parcours.step1Desc') },
    { icon: ClipboardList, num: 2, title: t('enterprisePage.parcours.step2Title'), desc: t('enterprisePage.parcours.step2Desc') },
    { icon: Zap, num: 3, title: t('enterprisePage.parcours.step3Title'), desc: t('enterprisePage.parcours.step3Desc') },
    { icon: Activity, num: 4, title: t('enterprisePage.parcours.step4Title'), desc: t('enterprisePage.parcours.step4Desc') },
  ];

  const partnerLogos = [
    { src: '/images/brugel2.png', alt: 'Brugel' },
    { src: '/images/carrefourv2.png', alt: 'Carrefour' },
    { src: '/images/Enerinvest.png', alt: 'Enerinvest' },
    { src: '/images/Ouzerie.png', alt: 'Ouzerie' },
    { src: '/images/Prehaut.png', alt: 'Préhaut' },
    { src: '/images/Stephenson.png', alt: 'Stephenson' },
    { src: '/images/Sun4school.png', alt: 'Sun4School' },
    { src: '/images/sibelga2.png', alt: 'Sibelga' },
  ];

  return (
    <>
      <SEOHead
        title="Entreprises | Sun Is Up - Partage d'Énergie pour Professionnels"
        description="Réduisez votre facture d'électricité grâce au partage d'énergie solaire locale. Solutions sur mesure pour entreprises à Bruxelles."
        keywords="partage énergie entreprises bruxelles, réduction facture électricité professionnels, communauté énergie B2B, énergie solaire entreprises belgique"
        url="https://sunisup.be/simulation"
        logo="https://sunisup.be/images/logo.png"
      />

      <div className="min-h-screen bg-white font-sans">
        {/* 1. Hero Section */}
        <section className="relative w-full pt-[180px] md:pt-[108px] pb-12 md:pb-0" style={{ backgroundColor: '#FFF5E1', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, #000000 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>

          <div className="w-full px-4 sm:px-6 lg:pl-8 lg:pr-0 relative z-10">
            <div className="flex flex-col items-center md:flex-row md:items-center gap-8 md:gap-4 lg:gap-6">
              <div className="flex-1 min-w-0 text-left md:pl-0">
                <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 md:mb-10" style={{ color: '#1a1a1a', lineHeight: '1.2' }}>
                  {t('enterprise.hero.titleLine1')}<br />{t('enterprise.hero.titleLine2')}
                </h1>
                <p className="text-lg sm:text-xl leading-relaxed mb-8" style={{ color: '#1a1a1a' }}>
                  {t('enterprise.hero.subtitleLine1')}<br />{t('enterprise.hero.subtitleLine2')}
                </p>
                <a
                  href="#cta-rapide"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-0.5 text-base"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  <Clock className="w-5 h-5" />
                  Prendre un rendez-vous
                </a>
              </div>

              <div className="flex-shrink-0 flex justify-center md:justify-end w-full md:w-auto md:pr-0">
                <div className="w-64 h-64 sm:w-72 sm:h-72 md:w-64 md:h-64 lg:w-[380px] lg:h-[380px] xl:w-[430px] xl:h-[430px] md:-translate-y-[40px] lg:-translate-y-[40px] xl:-translate-y-[45px]">
                  <NetworkSphere />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Bandeau de 3 chiffres clés */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)' }}>
                  <Zap className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold" style={{ color: '#1a1a1a' }}>1200+ MWh</h3>
                <p className="text-sm font-medium mt-1" style={{ color: '#666666' }}>{t('enterprisePage.stats.shared')}</p>
              </div>
              <div>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)' }}>
                  <Building2 className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold" style={{ color: '#1a1a1a' }}>80+</h3>
                <p className="text-sm font-medium mt-1" style={{ color: '#666666' }}>{t('enterprisePage.stats.members')}</p>
              </div>
              <div>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)' }}>
                  <Target className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold" style={{ color: '#1a1a1a' }}>5 GWh</h3>
                <p className="text-sm font-medium mt-1" style={{ color: '#666666' }}>{t('enterprisePage.stats.goal')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* 2b. Call-to-action rapide */}
        <section id="cta-rapide" className="py-16 sm:py-24" style={{ backgroundColor: '#fafafa' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
                Prêt à réduire votre facture énergétique ?
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: '#4a4a4a' }}>
                Choisissez l'option qui vous convient. Notre équipe vous répond rapidement.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
              <div className="p-6 rounded-2xl text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1" style={{ backgroundColor: 'white', border: '1px solid #e5e5e5' }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                  <Clock className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#1a1a1a' }}>RDV de 15 min</h3>
                <p className="text-sm mb-6" style={{ color: '#666666' }}>Un appel rapide pour évaluer vos économies potentielles.</p>
                <button
                  onClick={() => handleContactClick()}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold whitespace-nowrap transition-all duration-300 hover:shadow-lg"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  <Calendar className="w-4 h-4" />
                  Réserver un appel
                </button>
              </div>

              <div className="p-6 rounded-2xl text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1" style={{ backgroundColor: 'white', border: '1px solid #e5e5e5' }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                  <Calculator className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#1a1a1a' }}>Simuler mes économies</h3>
                <p className="text-sm mb-6" style={{ color: '#666666' }}>Estimez en ligne ce que vous pourriez économiser.</p>
                <button
                  onClick={() => {
                    setSimulationInitialRole(userRole);
                    setShowSimulationModal(true);
                  }}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold whitespace-nowrap transition-all duration-300 hover:shadow-lg"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  <Calculator className="w-4 h-4" />
                  Lancer la simulation
                </button>
              </div>

              <div className="p-6 rounded-2xl text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1" style={{ backgroundColor: 'white', border: '1px solid #e5e5e5' }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                  <PhoneCall className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#1a1a1a' }}>Nous contacter</h3>
                <p className="text-sm mb-6" style={{ color: '#666666' }}>Une question ? Écrivez-nous, nous répondons sous 24h.</p>
                <button
                  onClick={() => handleContactClick()}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold whitespace-nowrap transition-all duration-300 hover:shadow-lg"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  <PhoneCall className="w-4 h-4" />
                  Envoyer un message
                </button>
              </div>
              <div className="p-6 rounded-2xl text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1" style={{ backgroundColor: 'white', border: '1px solid #e5e5e5' }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                  <UserPlus className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#1a1a1a' }}>Nous rejoindre</h3>
                <p className="text-sm mb-6" style={{ color: '#666666' }}>Adhérez en ligne. Le contrat ne vous engage qu’une fois signé.</p>
                <button
                  onClick={() => navigate('/nous-rejoindre')}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold whitespace-nowrap transition-all duration-300 hover:shadow-lg"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  <UserPlus className="w-4 h-4" />
                  Rejoindre
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Comment fonctionne le partage d'énergie (schéma) */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                <Zap className="w-6 h-6" style={{ color: '#F8BD3D' }} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold" style={{ color: '#1a1a1a' }}>
                {t('enterprise.howItWorks.title')}
              </h2>
            </div>
            <p className="text-lg mb-10 max-w-3xl" style={{ color: '#4a4a4a' }}>
              {t('enterprise.howItWorks.subtitle')}
            </p>

            <EnergySharingDiagram />
          </div>
        </section>

        {/* 4. Avantages du partage (merged: toggle + enriched consumer tab) */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                <TrendingDown className="w-6 h-6" style={{ color: '#F8BD3D' }} />
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold" style={{ color: '#1a1a1a' }}>
                {t('enterprise.billing.title')}
              </h2>
            </div>
            <p className="text-lg mb-10 max-w-3xl" style={{ color: '#4a4a4a' }}>
              {t('enterprise.billing.subtitle')}
            </p>

            {/* Toggle Producteur/Consommateur */}
            <div className="flex justify-center mb-12">
              <div className="inline-flex rounded-full p-1" style={{ backgroundColor: '#f5f5f5' }}>
                <button
                  onClick={() => setUserRole('consumer')}
                  className="px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center gap-2"
                  style={{
                    backgroundColor: userRole === 'consumer' ? '#F8BD3D' : 'transparent',
                    color: userRole === 'consumer' ? '#1a1a1a' : '#666666'
                  }}
                >
                  <Building2 className="w-5 h-5" />
                  {t('enterprise.billing.consumeTab')}
                </button>
                <button
                  onClick={() => setUserRole('producer')}
                  className="px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center gap-2"
                  style={{
                    backgroundColor: userRole === 'producer' ? '#F8BD3D' : 'transparent',
                    color: userRole === 'producer' ? '#1a1a1a' : '#666666'
                  }}
                >
                  <Sun className="w-5 h-5" />
                  {t('enterprise.billing.produceTab')}
                </button>
              </div>
            </div>

            {/* Points clés dynamiques */}
            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              {(userRole === 'consumer' ? consumerBenefits : producerBenefits).map((benefit, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl transition-all duration-300 hover:shadow-lg"
                  style={{ backgroundColor: '#fafafa', border: '1px solid #e5e5e5' }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                      <benefit.icon className="w-6 h-6" style={{ color: '#F8BD3D' }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#1a1a1a' }}>{benefit.title}</h3>
                      <p style={{ color: '#666666' }}>{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Enriched consumer tab with green energy cards */}
            {userRole === 'consumer' && (
              <div className="grid lg:grid-cols-3 gap-8 mb-12">
                <div className="p-6 rounded-2xl text-center" style={{ backgroundColor: '#fafafa', border: '1px solid #e5e5e5' }}>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(21, 167, 89, 0.15)' }}>
                    <Target className="w-8 h-8" style={{ color: '#15A759' }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#1a1a1a' }}>{t('enterprise.greenEnergy.card1Title')}</h3>
                  <p style={{ color: '#666666' }}>{t('enterprise.greenEnergy.card1Desc')}</p>
                </div>
                <div className="p-6 rounded-2xl text-center" style={{ backgroundColor: '#fafafa', border: '1px solid #e5e5e5' }}>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(21, 167, 89, 0.15)' }}>
                    <BarChart3 className="w-8 h-8" style={{ color: '#15A759' }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#1a1a1a' }}>{t('enterprise.greenEnergy.card2Title')}</h3>
                  <p style={{ color: '#666666' }}>{t('enterprise.greenEnergy.card2Desc')}</p>
                </div>
                <div className="p-6 rounded-2xl text-center" style={{ backgroundColor: '#fafafa', border: '1px solid #e5e5e5' }}>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(21, 167, 89, 0.15)' }}>
                    <CheckCircle className="w-8 h-8" style={{ color: '#15A759' }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#1a1a1a' }}>{t('enterprise.greenEnergy.card3Title')}</h3>
                  <p style={{ color: '#666666' }}>{t('enterprise.greenEnergy.card3Desc')}</p>
                </div>
              </div>
            )}

            <div className="mb-10">
              <BeforeAfterSlider role={userRole} />
            </div>

            <div className="text-center">
              <button
                onClick={() => {
                  setSimulationInitialRole(userRole);
                  setShowSimulationModal(true);
                }}
                className="px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-3"
                style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
              >
                {t('enterprisePage.ctaButton')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>

        {/* 5. Votre parcours, de A à Z */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#fafafa' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                <HeartHandshake className="w-6 h-6" style={{ color: '#F8BD3D' }} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold" style={{ color: '#1a1a1a' }}>
                {t('enterprisePage.parcours.title')}
              </h2>
            </div>
            <p className="text-lg mb-12 max-w-3xl" style={{ color: '#4a4a4a' }}>
              {t('enterprisePage.parcours.subtitle')}
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {parcoursSteps.map((step) => (
                <div key={step.num} className="text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#F8BD3D' }}>
                    <span className="text-white text-2xl font-bold">{step.num}</span>
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)' }}>
                    <step.icon className="w-5 h-5" style={{ color: '#F8BD3D' }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#1a1a1a' }}>{step.title}</h3>
                  <p className="text-sm" style={{ color: '#666666' }}>{step.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <button
                onClick={() => navigate('/nous-rejoindre')}
                className="px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-3"
                style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
              >
                <UserPlus className="w-5 h-5" />
                Nous rejoindre
              </button>
            </div>

            <div className="mt-12 rounded-3xl p-8 sm:p-10 text-center" style={{ backgroundColor: '#FFF5E1', border: '1px solid rgba(248, 189, 61, 0.35)' }}>
              <h3 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: '#1a1a1a' }}>
                {t('enterprisePage.parcours.reassuranceTitle')}
              </h3>
              <p className="text-lg leading-relaxed max-w-3xl mx-auto mb-7" style={{ color: '#4a4a4a' }}>
                {t('enterprisePage.parcours.reassuranceText')}
              </p>
              <button
                onClick={() => handleContactClick()}
                className="px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-3"
                style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
              >
                {t('enterprisePage.parcours.contactButton')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>

        {/* 7. Créez votre propre communauté d'énergie */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#fafafa' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                <HeartHandshake className="w-6 h-6" style={{ color: '#F8BD3D' }} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold" style={{ color: '#1a1a1a' }}>
                {t('enterprise.community.title')}
              </h2>
            </div>
            <p className="text-lg mb-10 max-w-3xl" style={{ color: '#4a4a4a' }}>
              {t('enterprise.community.subtitle')}
            </p>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="space-y-6">
                  {[1, 2, 3, 4].map((step) => (
                    <div key={step} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F8BD3D' }}>
                        <span className="text-white font-bold">{step}</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-1" style={{ color: '#1a1a1a' }}>
                          {t(`enterprise.community.step${step}Title`)}
                        </h3>
                        <p style={{ color: '#666666' }}>
                          {t(`enterprise.community.step${step}Desc`)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-10">
                  <button
                    onClick={() => handleContactClick()}
                    className="px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-3"
                    style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                  >
                    {t('enterprisePage.ctaButton')}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-8 rounded-3xl" style={{ backgroundColor: 'white', border: '1px solid #e5e5e5' }}>
                <h3 className="text-xl font-semibold mb-6 text-center" style={{ color: '#1a1a1a' }}>
                  {t('enterprise.community.examplesTitle')}
                </h3>
                <div className="space-y-4">
                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFF5E1' }}>
                    <div className="flex items-center gap-3 mb-2">
                      <Factory className="w-5 h-5" style={{ color: '#F8BD3D' }} />
                      <span className="font-semibold" style={{ color: '#1a1a1a' }}>{t('enterprise.community.example1Title')}</span>
                    </div>
                    <p className="text-sm" style={{ color: '#666666' }}>{t('enterprise.community.example1Desc')}</p>
                  </div>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFF5E1' }}>
                    <div className="flex items-center gap-3 mb-2">
                      <Building2 className="w-5 h-5" style={{ color: '#F8BD3D' }} />
                      <span className="font-semibold" style={{ color: '#1a1a1a' }}>{t('enterprise.community.example2Title')}</span>
                    </div>
                    <p className="text-sm" style={{ color: '#666666' }}>{t('enterprise.community.example2Desc')}</p>
                  </div>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFF5E1' }}>
                    <div className="flex items-center gap-3 mb-2">
                      <Users className="w-5 h-5" style={{ color: '#F8BD3D' }} />
                      <span className="font-semibold" style={{ color: '#1a1a1a' }}>{t('enterprise.community.example3Title')}</span>
                    </div>
                    <p className="text-sm" style={{ color: '#666666' }}>{t('enterprise.community.example3Desc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. Aperçu de Nos réalisations */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                <MapPin className="w-6 h-6" style={{ color: '#F8BD3D' }} />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
              {t('enterprisePage.realisations.title')}
            </h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: '#4a4a4a' }}>
              {t('enterprisePage.realisations.desc')}
            </p>
            <button
              onClick={() => navigate('/nos-realisations')}
              className="px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-3"
              style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
            >
              {t('enterprisePage.realisations.button')}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* 9. Nos partenaires */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#fafafa' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ color: '#1a1a1a' }}>
                {t('about.partners.title')}
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-6 items-center justify-items-center">
              {partnerLogos.map((logo, index) => (
                <div key={index} className="bg-orange-100 p-2 sm:p-3 rounded-lg shadow-sm border border-orange-200">
                  <img src={logo.src} alt={logo.alt} className="h-16 w-16 sm:h-20 sm:w-20 object-contain opacity-90 hover:opacity-100 transition-opacity duration-300" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. CTA Final */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#1a1a1a' }}>
              {t('enterprise.cta.title')}
            </h2>
            <p className="text-xl mb-10" style={{ color: '#4a4a4a' }}>
              {t('enterprise.cta.subtitle')}
            </p>
            <button
              onClick={() => handleContactClick()}
              className="px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-3"
              style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
            >
              {t('enterprisePage.ctaButton')}
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </section>
      </div>

      <SimulationModal
        isOpen={showSimulationModal}
        onClose={() => setShowSimulationModal(false)}
        initialRole={simulationInitialRole}
      />
    </>
  );
}
